import React, { ReactNode, useRef } from 'react';
import { CSSProp } from 'styled-components';
import classNames from 'classnames';

export interface InputOnlyProps {
  error?: boolean | string;
  fullWidth?: boolean;
  inputClassName?: string;
  inputCss?: CSSProp;
  leftAdornment?: ReactNode;
  rightAdornment?: ReactNode;
}

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> &
  InputOnlyProps & {
    css?: CSSProp;
  };

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      children,
      className,
      css,
      disabled,
      error,
      fullWidth,
      inputClassName,
      inputCss,
      leftAdornment,
      onClick,
      rightAdornment,
      style,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(
      typeof ref !== 'function' ? ref?.current! : null,
    );

    function handleContainerClick(e: React.MouseEvent<HTMLDivElement>) {
      if (!e.isDefaultPrevented() && inputRef.current) {
        inputRef.current.focus();
      }
    }

    return (
      <div
        className={classNames(
          'flex flex-grow items-center h-10 border focus-within:border-green-500 rounded focus-within:ring',
          {
            'w-full': fullWidth,
            'border-red-500 ring-red-500': error,
            'rounded bg-white': !error,
            'bg-grey-300 text-grey-700 bg-white cursor-not-allowed': disabled,
          },
          className,
        )}
        css={css}
        onClick={handleContainerClick}
        style={style}
      >
        {leftAdornment}

        <input
          css={inputCss}
          disabled={disabled}
          className={classNames(
            'placeholder-slate-green-500 w-full h-full text-base bg-transparent border-transparent rounded outline-none appearance-none',
            leftAdornment && rightAdornment
              ? 'px-2'
              : leftAdornment
              ? 'pl-2 pr-4'
              : rightAdornment
              ? 'pl-4 pr-2'
              : 'px-4',
            inputClassName,
          )}
          {...props}
          aria-invalid={error ? true : undefined}
          ref={r => {
            if (typeof ref === 'function') {
              ref(r);
            } else if (ref) {
              // @ts-ignore
              ref.current = r;
            }
            // @ts-ignore
            inputRef.current = r;
          }}
        >
          {children}
        </input>

        {rightAdornment}
      </div>
    );
  },
);
